"use client";

import React from "react";
import { Tooltip } from "@/ui";
import { colors, spacing, radius } from "@/ui/tokens";
import {
  Translate,
  QuestionAnswer,
  FileDownload,
  FindInPage,
  AutoFixHigh,
  Schedule,
  Email,
  Analytics,
  Refresh,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useSOLAFloating } from "./context";

export function QuickActions() {
  const { state, executeQuickAction } = useSOLAFloating();

  // Get context-specific quick actions
  const getQuickActions = () => {
    const baseActions = [
      {
        id: "help",
        icon: <QuestionAnswer style={{ fontSize: 16 }} />,
        label: "Get Help",
        category: "general",
      },
      {
        id: "export",
        icon: <FileDownload style={{ fontSize: 16 }} />,
        label: "Export Data",
        category: "data",
      },
    ];

    switch (state.pageContext.pageType) {
      case "candidate":
        return [
          ...baseActions,
          {
            id: "translate-profile",
            icon: <Translate style={{ fontSize: 16 }} />,
            label: "Translate Profile",
            category: "localization",
          },
          {
            id: "generate-interview",
            icon: <AutoFixHigh style={{ fontSize: 16 }} />,
            label: "Generate Interview Questions",
            category: "automation",
          },
          {
            id: "find-similar",
            icon: <FindInPage style={{ fontSize: 16 }} />,
            label: "Find Similar Candidates",
            category: "analysis",
          },
          {
            id: "schedule-interview",
            icon: <Schedule style={{ fontSize: 16 }} />,
            label: "Schedule Interview",
            category: "communication",
          },
        ];

      case "job":
        return [
          ...baseActions,
          {
            id: "find-candidates",
            icon: <FindInPage style={{ fontSize: 16 }} />,
            label: "Find Matching Candidates",
            category: "analysis",
          },
          {
            id: "generate-description",
            icon: <AutoFixHigh style={{ fontSize: 16 }} />,
            label: "Generate Job Description",
            category: "automation",
          },
          {
            id: "analyze-requirements",
            icon: <Analytics style={{ fontSize: 16 }} />,
            label: "Analyze Requirements",
            category: "analysis",
          },
        ];

      case "company":
        return [
          ...baseActions,
          {
            id: "company-analysis",
            icon: <Analytics style={{ fontSize: 16 }} />,
            label: "Analyze Company",
            category: "analysis",
          },
          {
            id: "find-company-candidates",
            icon: <FindInPage style={{ fontSize: 16 }} />,
            label: "Find Ex-Employees",
            category: "analysis",
          },
          {
            id: "company-outreach",
            icon: <Email style={{ fontSize: 16 }} />,
            label: "Generate Outreach Strategy",
            category: "communication",
          },
        ];

      case "dashboard":
        return [
          ...baseActions,
          {
            id: "refresh-data",
            icon: <Refresh style={{ fontSize: 16 }} />,
            label: "Refresh Data",
            category: "data",
          },
          {
            id: "generate-report",
            icon: <Analytics style={{ fontSize: 16 }} />,
            label: "Generate Report",
            category: "analysis",
          },
        ];

      default:
        return baseActions;
    }
  };

  const quickActions = getQuickActions();

  const handleQuickAction = async (actionId) => {
    try {
      await executeQuickAction(actionId);
    } catch (error) {
      console.error("Failed to execute quick action:", error);
    }
  };

  if (quickActions.length === 0) return null;

  return (
    <div
      style={{
        padding: spacing[2],
        borderBottom: `1px solid ${colors.border.subtle}`,
        backgroundColor: colors.canvas.card,
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: spacing[1],
          justifyContent: "center",
        }}
      >
        <AnimatePresence>
          {quickActions.map((action, index) => (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -10 }}
              transition={{
                delay: index * 0.05,
                type: "spring",
                stiffness: 400,
                damping: 25,
              }}
            >
              <Tooltip content={action.label} placement="top">
                <button
                  onClick={() => handleQuickAction(action.id)}
                  style={{
                    width: 32,
                    height: 32,
                    border: `1px solid ${colors.border.subtle}`,
                    borderRadius: radius.sm,
                    backgroundColor: colors.canvas.default,
                    color: colors.text.muted,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.action.productLight;
                    e.currentTarget.style.color = colors.action.product;
                    e.currentTarget.style.borderColor = colors.action.product;
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.canvas.default;
                    e.currentTarget.style.color = colors.text.muted;
                    e.currentTarget.style.borderColor = colors.border.subtle;
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {action.icon}
                </button>
              </Tooltip>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
